import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Share2,
  ArrowRight,
  ArrowLeft,
  Trophy,
  Brain,
  Shield,
  Zap,
  Users,
  Home,
  Briefcase,
  Heart
} from 'lucide-react'
import toast from 'react-hot-toast'

/**
 * Survival Personality Quiz
 * Viral BuzzFeed-style quiz to determine survival personality type
 */

const questions = [
  {
    id: 1,
    question: "Emergency alert sounds. Your first instinct?",
    answers: [
      { text: "Check social media for updates", type: "networker" },
      { text: "Grab my go-bag immediately", type: "prepper" },
      { text: "Rally the family/neighbors", type: "leader" },
      { text: "Calculate the safest route out", type: "strategist" }
    ]
  },
  {
    id: 2,
    question: "You can save ONE item from your home. What is it?",
    answers: [
      { text: "Water purification tablets", type: "prepper" },
      { text: "Ham radio for communication", type: "networker" },
      { text: "First aid kit", type: "leader" },
      { text: "Survival manual", type: "strategist" }
    ]
  },
  {
    id: 3,
    question: "Power's been out for 3 days. Your priority?",
    answers: [
      { text: "Find other survivors to team up", type: "networker" },
      { text: "Ration remaining supplies carefully", type: "strategist" },
      { text: "Set up a neighborhood watch", type: "leader" },
      { text: "Use my generator I prepared", type: "prepper" }
    ]
  },
  {
    id: 4,
    question: "You find an abandoned store. What do you take first?",
    answers: [
      { text: "Medical supplies for the group", type: "leader" },
      { text: "Non-perishable food", type: "prepper" },
      { text: "Batteries and electronics", type: "networker" },
      { text: "Maps and navigation tools", type: "strategist" }
    ]
  },
  {
    id: 5,
    question: "A stranger asks to join your shelter. You:",
    answers: [
      { text: "Assess their skills first", type: "strategist" },
      { text: "Welcome them - strength in numbers", type: "leader" },
      { text: "Trade info before deciding", type: "networker" },
      { text: "Politely decline - supplies are limited", type: "prepper" }
    ]
  },
  {
    id: 6,
    question: "Your survival motto would be:",
    answers: [
      { text: "Prepare for the worst, hope for the best", type: "prepper" },
      { text: "Information is power", type: "networker" },
      { text: "No one left behind", type: "leader" },
      { text: "Think twice, act once", type: "strategist" }
    ]
  },
  {
    id: 7,
    question: "In peacetime, you're most likely found:",
    answers: [
      { text: "Organizing community events", type: "leader" },
      { text: "Playing strategy games", type: "strategist" },
      { text: "Scrolling news and social feeds", type: "networker" },
      { text: "Stocking your emergency kit", type: "prepper" }
    ]
  },
  {
    id: 8,
    question: "Your biggest fear in a crisis?",
    answers: [
      { text: "Running out of supplies", type: "prepper" },
      { text: "Being cut off from information", type: "networker" },
      { text: "Losing people I care about", type: "leader" },
      { text: "Making the wrong decision", type: "strategist" }
    ]
  }
]

const personalityTypes = {
  prepper: {
    title: "The Doomsday Prepper",
    emoji: "🛡️",
    icon: Shield,
    color: "bg-green-500",
    description: "You're the one everyone wishes they knew when disaster strikes. While others panic, you're calmly distributing supplies from your 3-year stockpile.",
    traits: ["Always prepared", "Self-sufficient", "Resource manager", "Equipment expert"],
    survivalRate: "94%",
    famousExample: "Bear Grylls meets Ron Swanson",
    advice: "Your preparation is admirable, but remember: even the best prepper needs allies. Share your knowledge!"
  },
  networker: {
    title: "The Information Broker",
    emoji: "📡",
    icon: Zap,
    color: "bg-blue-500",
    description: "You're the human internet when the grid goes down. Your superpower? Knowing exactly what's happening and who to talk to about it.",
    traits: ["Well-connected", "Info gatherer", "Communication expert", "Social navigator"],
    survivalRate: "87%",
    famousExample: "Post-apocalyptic journalist",
    advice: "Information is power, but verify your sources! In chaos, misinformation spreads faster than fire."
  },
  leader: {
    title: "The Natural Leader",
    emoji: "👥",
    icon: Users,
    color: "bg-purple-500",
    description: "When society crumbles, you build it back up. People naturally look to you for guidance, and you never leave anyone behind.",
    traits: ["Inspiring", "Protective", "Decision maker", "Team builder"],
    survivalRate: "91%",
    famousExample: "Rick Grimes with better decision-making",
    advice: "Your compassion is your strength, but remember: you can't save everyone. Sometimes tough choices are necessary."
  },
  strategist: {
    title: "The Master Strategist",
    emoji: "🧠",
    icon: Brain,
    color: "bg-orange-500",
    description: "While others react, you're already three steps ahead. Your analytical mind turns chaos into calculated survival.",
    traits: ["Analytical", "Planning expert", "Risk calculator", "Problem solver"],
    survivalRate: "89%",
    famousExample: "Apocalyptic chess master",
    advice: "Your plans are brilliant, but stay flexible. In survival, the ability to adapt beats the perfect plan."
  }
}

const PersonalityQuiz = () => {
  const navigate = useNavigate()
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [answers, setAnswers] = useState([])
  const [showResult, setShowResult] = useState(false)
  const [personalityType, setPersonalityType] = useState(null)
  const [isAnimating, setIsAnimating] = useState(false)

  const handleAnswer = (type) => {
    const newAnswers = [...answers, type]
    setAnswers(newAnswers)

    if (currentQuestion < questions.length - 1) {
      setIsAnimating(true)
      setTimeout(() => {
        setCurrentQuestion(currentQuestion + 1)
        setIsAnimating(false)
      }, 300)
    } else {
      calculateResult(newAnswers)
    }
  }

  const calculateResult = (allAnswers) => {
    const typeCounts = allAnswers.reduce((acc, type) => {
      acc[type] = (acc[type] || 0) + 1
      return acc
    }, {})

    const dominantType = Object.entries(typeCounts).reduce((a, b) => 
      typeCounts[a[0]] > typeCounts[b[0]] ? a : b
    )[0]

    setPersonalityType(dominantType)
    setShowResult(true)
  }

  const shareResult = () => {
    const result = personalityTypes[personalityType]
    const shareText = `I'm ${result.title} ${result.emoji} in the Survival Personality Quiz! My survival rate: ${result.survivalRate}. What's your survival personality?`
    
    if (navigator.share) {
      navigator.share({
        title: 'My Survival Personality',
        text: shareText,
        url: window.location.origin
      }).catch(() => {})
    } else {
      navigator.clipboard.writeText(shareText)
      toast.success('Result copied to clipboard!')
    }
  }

  const restartQuiz = () => {
    setCurrentQuestion(0)
    setAnswers([])
    setShowResult(false)
    setPersonalityType(null)
  }

  if (showResult && personalityType) {
    const result = personalityTypes[personalityType]
    const Icon = result.icon

    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="max-w-2xl w-full"
        >
          {/* Result Card */}
          <div className="bg-gray-900 rounded-2xl p-8 text-center">
            {/* Icon */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className={`w-32 h-32 ${result.color} rounded-full flex items-center justify-center mx-auto mb-6`}
            >
              <Icon className="w-16 h-16 text-white" />
            </motion.div>

            {/* Title */}
            <motion.h1
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="text-4xl font-bold text-white mb-2"
            >
              You are...
            </motion.h1>
            
            <motion.h2
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-5xl font-bold mb-6 bg-gradient-to-r from-orange-400 to-red-500 bg-clip-text text-transparent"
            >
              {result.title}
            </motion.h2>

            {/* Description */}
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-lg text-gray-300 mb-8 leading-relaxed"
            >
              {result.description}
            </motion.p>

            {/* Stats */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="grid grid-cols-2 gap-4 mb-8"
            >
              <div className="bg-gray-800 rounded-lg p-4">
                <div className="text-3xl font-bold text-green-400 mb-1">
                  {result.survivalRate}
                </div>
                <div className="text-sm text-gray-400">Survival Rate</div>
              </div>
              <div className="bg-gray-800 rounded-lg p-4">
                <div className="text-2xl mb-1">{result.emoji}</div>
                <div className="text-sm text-gray-400">Spirit Animal</div>
              </div>
            </motion.div>

            {/* Traits */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.7 }}
              className="mb-8"
            >
              <h3 className="text-lg font-semibold text-white mb-3">Your Traits</h3>
              <div className="flex flex-wrap gap-2 justify-center">
                {result.traits.map((trait, index) => (
                  <span
                    key={index}
                    className="px-3 py-1 bg-gray-800 rounded-full text-sm text-gray-300"
                  >
                    {trait}
                  </span>
                ))}
              </div>
            </motion.div>

            {/* Famous Example */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.8 }}
              className="bg-gray-800 rounded-lg p-4 mb-6"
            >
              <p className="text-sm text-gray-400">You're basically:</p>
              <p className="text-white font-medium">{result.famousExample}</p>
            </motion.div>

            {/* Advice */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.9 }}
              className="bg-orange-900 bg-opacity-20 border border-orange-800 rounded-lg p-4 mb-8"
            >
              <p className="text-sm text-orange-300">{result.advice}</p>
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1 }}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <button
                onClick={shareResult}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors"
              >
                <Share2 className="w-5 h-5" />
                Share Result
              </button>
              <button
                onClick={restartQuiz}
                className="px-6 py-3 bg-gray-800 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors"
              >
                Retake Quiz
              </button>
              <button
                onClick={() => navigate('/')}
                className="flex items-center justify-center gap-2 px-6 py-3 bg-gray-800 text-white rounded-lg font-medium hover:bg-gray-700 transition-colors"
              >
                <Home className="w-5 h-5" />
                Go Home
              </button>
            </motion.div>
          </div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-950 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">
            Survival Personality Quiz
          </h1>
          <p className="text-gray-400">
            Discover your apocalypse alter-ego in 8 questions
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <div className="bg-gray-800 rounded-full h-2 overflow-hidden">
            <motion.div
              className="bg-gradient-to-r from-orange-500 to-red-500 h-full"
              initial={{ width: 0 }}
              animate={{ width: `${((currentQuestion + 1) / questions.length) * 100}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
          <p className="text-center text-sm text-gray-500 mt-2">
            Question {currentQuestion + 1} of {questions.length}
          </p>
        </div>

        {/* Question Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            initial={{ x: 50, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -50, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="bg-gray-900 rounded-2xl p-8"
          >
            <h2 className="text-2xl font-bold text-white mb-8">
              {questions[currentQuestion].question}
            </h2>

            <div className="space-y-4">
              {questions[currentQuestion].answers.map((answer, index) => (
                <motion.button
                  key={index}
                  initial={{ y: 20, opacity: 0 }}
                  animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => !isAnimating && handleAnswer(answer.type)}
                  disabled={isAnimating}
                  className="w-full text-left p-4 bg-gray-800 rounded-lg hover:bg-gray-700 transition-all duration-200 group"
                >
                  <span className="text-lg text-white group-hover:text-orange-400 transition-colors">
                    {answer.text}
                  </span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Navigation */}
        <div className="flex justify-between mt-6">
          <button
            onClick={() => currentQuestion > 0 && setCurrentQuestion(currentQuestion - 1)}
            disabled={currentQuestion === 0}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
              currentQuestion === 0
                ? 'bg-gray-800 text-gray-600 cursor-not-allowed'
                : 'bg-gray-800 text-white hover:bg-gray-700'
            }`}
          >
            <ArrowLeft className="w-4 h-4" />
            Previous
          </button>

          <button
            onClick={() => navigate('/')}
            className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Exit Quiz
          </button>
        </div>
      </div>
    </div>
  )
}

export default PersonalityQuiz